import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import useCalendarPreferences from "app/utils/hooks/useCalendarPreferences";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { DateTime } from "luxon";

import { RootState } from "typedefs";
import { CoachProfileModel } from "app/models/scheduling/CoachProfileModel";
import { PackageCredit } from "app/utils/creditBundles";
import { setBookSession } from "app/actions/scheduling/bookSession";
import { Loading } from "app/components/Wrappers";
import SessionsDatePicker from "../../DateTimeCoachSlots/_components/SessionsDatePicker";

import styles from "./FacilityCalendarBody.module.scss";
import useFacilityAvailability from "./useFacilityAvailability";
import { nextAvailableDate } from "./byDateAvailability";
import {
  coachIdsWithSlots,
  gridRows,
  hourLabel,
  slotAt,
  snapSlotsToInterval,
  stepDay,
} from "./facilityCalendarGrid";
import {
  BookingFilterState,
  EMPTY_FILTERS,
  SESSION_TYPE_CLINICS,
  SESSION_TYPE_SESSIONS,
} from "./BookingFilters";
import CoachAvatar from "./CoachAvatar";
import useFacilityPrograms from "./useFacilityPrograms";
import { topRateForAthlete } from "./coachMeta";

interface Props {
  coaches: CoachProfileModel[];
  /** Coaches the header's search leaves visible; narrows the columns shown. */
  visibleCoachIds?: string[];
  packageCredits?: Map<number, PackageCredit>;
  /** Where a chosen booking continues to — see FacilityBooking. */
  bookingRoute?: (coach: CoachProfileModel, rateId?: number) => string;
  /** See FacilityBooking — decides which scheduling surface to ask. */
  isPublicPage?: boolean;
  /** The filter row above; this screen honours its coach selection. */
  filters?: BookingFilterState;
}

/**
 * The Facility Calendar tab (frame 526:29178): the facility's day laid out as a
 * column per coach and a row per start time, with the hour down the left gutter
 * and empty cells where a coach is not free.
 *
 * Per comment #29 this is one day at a time — the frames draw an infinitely
 * scrolling multi-day grid, but that was settled against: "we should get rid of
 * the infinity scroll... display only a single day and they need to navigate
 * through the calendar". The `‹ Today ›` stepper moves between days that
 * actually have availability (#30).
 */
const FacilityCalendarBody: React.FC<Props> = ({
  coaches,
  visibleCoachIds,
  packageCredits,
  bookingRoute = (coach, rateId) =>
    `/public/availability?coach=${coach.calendarUuid}` +
    (rateId ? `&sessionId=${rateId}` : ""),
  isPublicPage = true,
  filters = EMPTY_FILTERS,
}) => {
  const { t } = useTranslation();
  const { preferences } = useCalendarPreferences();
  const dispatch = useDispatch();
  const history = useHistory();

  const [month, setMonth] = useState(DateTime.local().startOf("month"));
  const [date, setDate] = useState<string | null>(null);
  // The date in the header opens a month calendar: the stepper beside it moves a
  // day at a time, which is a lot of presses to reach a date next month.
  const [pickerOpen, setPickerOpen] = useState(false);
  const pickerRef = useRef<HTMLDivElement | null>(null);

  /**
   * CFTB-3435: the duration is asked of the endpoints, not just used to space the
   * grid's rows — a filter that only changed the rows drew the default option's
   * slots under someone else's label.
   */
  const filteredDuration =
    filters.duration === "all" ? null : Number(filters.duration) || null;

  /**
   * Session Type, which on this tab chooses between the two things the day holds:
   * the coaches' bookable times and the clinics and events running alongside them.
   * "All" is both, which is how the tab opens.
   */
  const showSessions = filters.sessionType !== SESSION_TYPE_CLINICS;
  const showClinics = filters.sessionType !== SESSION_TYPE_SESSIONS;

  const { availableDates, datesLoading, slotsByCoach, slotsLoading } =
    useFacilityAvailability(
      coaches,
      month,
      date,
      isPublicPage,
      filteredDuration,
    );

  useEffect(() => {
    if (!pickerOpen) {
      return undefined;
    }
    const onPointerDown = (event: MouseEvent) => {
      if (!pickerRef.current?.contains(event.target as Node)) {
        setPickerOpen(false);
      }
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setPickerOpen(false);
      }
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [pickerOpen]);

  /** The days the picker offers — the same set the stepper moves between. */
  const includeDates = useMemo(
    () => Array.from(availableDates).map((iso) => DateTime.fromISO(iso).toJSDate()),
    [availableDates],
  );

  useEffect(() => {
    if (date !== null || datesLoading || availableDates.size === 0) {
      return;
    }
    setDate(nextAvailableDate(availableDates, DateTime.local()));
  }, [availableDates, datesLoading, date]);

  // Hourly rows unless a duration is chosen, in which case the grid steps by it.
  const intervalMinutes = filteredDuration ?? 60;

  const visibleGrid = useMemo(() => {
    const searched = visibleCoachIds
      ? slotsByCoach.filter((row) => visibleCoachIds.includes(row.coachId))
      : slotsByCoach;
    const filtered =
      filters.coachId === "all"
        ? searched
        : searched.filter((row) => row.coachId === filters.coachId);
    // Showing clinics alone empties the columns rather than removing them: the
    // coaches stay on the grid, and their clinics keep the column they run in.
    if (!showSessions) {
      return filtered.map(({ coachId }) => ({ coachId, slots: [] }));
    }
    return snapSlotsToInterval(filtered, intervalMinutes);
  }, [
    slotsByCoach,
    filters.coachId,
    visibleCoachIds,
    intervalMinutes,
    showSessions,
  ]);

  // Every coach the filters leave in gets a column, whether or not they have
  // anything on this day — the frame shows their column with empty cells, which
  // is how an athlete sees who else is at the facility. Columns were previously
  // limited to coaches with slots, so a day where one coach happened to be free
  // came out as a single column.
  const columnCoaches = useMemo(() => {
    const ids = visibleGrid.map((row) => row.coachId);
    return coaches.filter((coach) => ids.includes(coach.id));
  }, [coaches, visibleGrid]);

  /**
   * CFTB-3437: the public programmes index looks a facility up by its **uuid** —
   * `Facility.find_by!(uuid: params[:facility_id], public_programs: true)` — while a
   * coach's `facilityId` is the facility's external id. The two are different values,
   * so every public request 404d and the hook, which falls back to an empty list,
   * drew no clinics at all. The uuid is on the facility this page loaded (it is also
   * what the page's own `?facilityId=` carries), so ask with that.
   *
   * In-app the private index takes no facility parameter; the coach's id stays there
   * purely as the "do we know a facility yet" guard the hook applies.
   */
  const facilityUuid = useSelector(
    (state: RootState) => state.bookSession?.facility?.uuid,
  );

  // Programmes on this day, which the frame draws as blocks in their coach's
  // column. A lesson or an unavailability draws nothing — that cell just stays
  // blank, as it does for any hour a coach is not offering.
  const allPrograms = useFacilityPrograms(
    isPublicPage ? facilityUuid : coaches[0]?.facilityId,
    date,
    isPublicPage,
  );

  const programs = useMemo(
    () => (showClinics ? allPrograms : []),
    [allPrograms, showClinics],
  );

  // Rows are the day's slot times plus any hour a programme starts on, so a
  // programme always has a row to sit in — including one nobody is bookable at,
  // which is exactly the case that had it silently dropped.
  const rows = useMemo(() => {
    const fromSlots = gridRows(visibleGrid, preferences?.timeFormat);
    const extra = programs
      .filter(
        (program) =>
          !fromSlots.some(
            (row) => row.startHour.toMillis() === program.start.toMillis(),
          ),
      )
      .map((program) => ({
        timeId: program.start.toMillis(),
        startHour: program.start,
        hourLabel: hourLabel(program.start, preferences?.timeFormat),
      }));

    return [...fromSlots, ...extra].sort((a, b) => a.timeId - b.timeId);
  }, [visibleGrid, programs]);

  /**
   * Where each programme sits: the row its start falls on, and how many rows it
   * covers. Rows are the day's start times, so a programme spans every row from
   * the one it starts on up to the one its end passes.
   */
  const programPlacements = useMemo(
    () =>
      programs
        .map((program) => {
          const startIndex = rows.findIndex(
            (row) => row.startHour >= program.start,
          );
          if (startIndex === -1) {
            return null;
          }
          const afterEnd = rows.findIndex((row) => row.startHour >= program.end);
          const endIndex = afterEnd === -1 ? rows.length : afterEnd;
          // By id where the payload carries one, by name where it does not: the
          // public programmes endpoint describes a programme's coaches as
          // names and a picture, with no id of any kind.
          const column = columnCoaches.find((coach) => {
            const byId = [coach.id, coach.externalId, coach.calendarUuid]
              .filter(Boolean)
              .map(String)
              .some((id) => program.coachIds.includes(id));

            const byName = program.coachNames.includes(
              (coach.fullName || "").trim().toLowerCase(),
            );

            return byId || byName;
          });
          if (!column) {
            return null;
          }
          const coachId = column.id;
          return {
            program,
            coachId,
            startIndex,
            span: Math.max(1, endIndex - startIndex),
          };
        })
        .filter(Boolean) as Array<{
        program: (typeof programs)[number];
        coachId: string;
        startIndex: number;
        span: number;
      }>,
    [programs, rows, columnCoaches],
  );

  /** The cells a programme covers, so they are not drawn underneath it. */
  const coveredCells = useMemo(() => {
    const covered = new Set<string>();
    programPlacements.forEach(({ coachId, startIndex, span }) => {
      for (let offset = 0; offset < span; offset += 1) {
        covered.add(`${coachId}-${startIndex + offset}`);
      }
    });
    return covered;
  }, [programPlacements]);

  // Whether anyone is free at all decides the empty state, rather than whether
  // every column has something.
  const anySlots = useMemo(
    () => coachIdsWithSlots(visibleGrid).length > 0 || programs.length > 0,
    [visibleGrid, programs],
  );

  const goToDay = (direction: 1 | -1) => {
    const target = stepDay(availableDates, date, direction);
    if (!target) {
      return;
    }
    setDate(target);
    // Stepping across a month boundary has to reload that month's days.
    const parsed = DateTime.fromISO(target);
    if (parsed.isValid && !parsed.startOf("month").equals(month)) {
      setMonth(parsed.startOf("month"));
    }
  };

  /**
   * CFTB-3453: Today goes to today.
   *
   * It used to ask for `nextAvailableDate(availableDates, now)`, and the days in
   * hand are the ones loaded around the month the picker is showing — that month
   * and one either side (see `useFacilityAvailability`). Open the picker, walk it
   * to a month further out, and that set holds no day from this month at all, so
   * "the soonest available day from today" resolved to the first available day of
   * *that* month: Today jumped into the future, which is the report.
   *
   * Asking for the current month here does not fix it on its own — the answer
   * arrives a request later, while the date has already been chosen from the old
   * set. Today is today, so it needs no availability to work it out; a day nobody
   * is free on shows the grid's own empty state, which is what today looks like.
   */
  const goToToday = () => {
    const today = DateTime.local();
    setMonth(today.startOf("month"));
    setDate(today.toISODate());
    setPickerOpen(false);
  };

  /**
   * A cell names the coach and the time, so clicking it carries both to the
   * coach's booking screen, which opens on that slot with its confirm bar —
   * where confirming books it. The option is the one that coach's calendar would
   * open on anyway (#31).
   *
   * `time` is the shape the booking flow stores: millis, not an ISO string, which
   * is what its confirm bar and the purchase call both read.
   */
  const selectCell = (coach: CoachProfileModel, startHour: DateTime) => {
    const topRate = topRateForAthlete(coach, packageCredits);
    const minutes = topRate?.durationInMinutes ?? intervalMinutes;

    dispatch(
      setBookSession({
        coach,
        date,
        time: {
          startHour: startHour.toMillis(),
          endHour: startHour.plus({ minutes }).toMillis(),
          timeZone: startHour.zoneName,
          coachId: coach.externalId,
        },
        duration: minutes,
        ...(topRate
          ? { pricingValue: topRate, selectedSession: topRate }
          : {}),
      }),
    );
    // CFTB-3448: a tapped cell settles both the session and the time, so the
    // calendar it opens on that slot (CFTB-3439) is asked for rather than assumed
    // from the URL.
    history.push(bookingRoute(coach, topRate?.id), { sessionPicked: true });
  };

  /**
   * CFTB-3436: the month's states are drawn inside the card, not in place of it.
   *
   * These were early returns, so navigating the day picker to a month with no
   * availability replaced the whole card — day bar and all — and the open picker
   * went with it. It read as the calendar closing itself, and left no control to
   * navigate back with, since the picker was the control.
   */
  const monthIsLoading = datesLoading && availableDates.size === 0;
  const monthHasNothing = !datesLoading && availableDates.size === 0;

  return (
    <>
      <div className={styles.calendar_card}>
        <div className={styles.day_bar}>
          {/* Frame 526:29219: the date carries a dropdown chevron, and the
              ‹ Today › controls sit at the other end of the bar. */}
          <div className={styles.day_picker} ref={pickerRef}>
            <button
              type="button"
              className={styles.day_title}
              onClick={() => setPickerOpen((open) => !open)}
              aria-expanded={pickerOpen}
              data-testid="facility-calendar-date"
            >
              <span>
                {date
                  ? DateTime.fromISO(date).toLocaleString(DateTime.DATE_FULL)
                  : ""}
              </span>
              <i
                className={`ico ico-chevron ${styles.day_title_chevron} ${
                  pickerOpen ? styles.day_title_chevron_open : ""
                }`}
              />
            </button>

            {pickerOpen && (
              <div className={styles.day_picker_panel}>
                <SessionsDatePicker
                  date={date ? DateTime.fromISO(date) : DateTime.local()}
                  handleChangeDate={(value: DateTime) => {
                    setDate(value.toISODate());
                    if (!value.startOf("month").equals(month)) {
                      setMonth(value.startOf("month"));
                    }
                    setPickerOpen(false);
                  }}
                  handleDecreaseMonth={(decreaseMonth) => {
                    setMonth(month.minus({ months: 1 }));
                    decreaseMonth?.();
                  }}
                  handleIncreaseMonth={(increaseMonth) => {
                    setMonth(month.plus({ months: 1 }));
                    increaseMonth?.();
                  }}
                  includeDates={includeDates}
                />
              </div>
            )}
          </div>
          <div className={styles.day_stepper}>
            <button
              type="button"
              className={styles.step_button}
              onClick={() => goToDay(-1)}
              disabled={!stepDay(availableDates, date, -1)}
              aria-label={t("Previous available day")}
            >
              <i className={`ico ico-chevron ${styles.step_prev}`} />
            </button>
            <button type="button" className={styles.today_button} onClick={goToToday}>
              {t("Today")}
            </button>
            <button
              type="button"
              className={styles.step_button}
              onClick={() => goToDay(1)}
              disabled={!stepDay(availableDates, date, 1)}
              aria-label={t("Next available day")}
            >
              <i className={`ico ico-chevron ${styles.step_next}`} />
            </button>
          </div>
        </div>

        {monthIsLoading ? (
          <div className={styles.loading_block}>
            <Loading isLoading loadType="spinner" />
          </div>
        ) : monthHasNothing ? (
          <div className={styles.empty_state}>
            {t("No coaches at this facility have availability right now.")}
          </div>
        ) : slotsLoading ? (
          <div className={styles.loading_block}>
            <Loading isLoading loadType="spinner" />
          </div>
        ) : !anySlots || columnCoaches.length === 0 ? (
          <div className={styles.empty_state}>
            {/* Say what was looked for: an athlete who asked for clinics is not
                being told about timeslots. */}
            {showSessions
              ? t("No available timeslots on the selected day.")
              : t("No clinics or events on the selected day.")}
          </div>
        ) : (
          <div className={styles.grid_scroll}>
            <div
              className={styles.grid}
              style={{
                gridTemplateColumns: `56px repeat(${columnCoaches.length}, minmax(120px, 1fr))`,
              }}
            >
              <div className={styles.gutter_head} />
              {columnCoaches.map((coach) => (
                <div key={coach.id} className={styles.column_head}>
                  <CoachAvatar
                    coach={coach}
                    className={styles.column_avatar}
                    fontSize={12}
                  />
                  <div className={styles.column_name}>{coach.fullName}</div>
                </div>
              ))}

              {/* The frame rules the whole grid — a line under every row and one
                  between each pair of columns — and lays the bookable slots on
                  top. Inert, so a line never swallows a click, and under the
                  cells and programme blocks — which say so themselves, since a
                  transformed element paints above static ones whatever the order
                  here. */}
              {rows.map((row, rowIndex) => (
                <div
                  key={`line-${row.timeId}`}
                  className={styles.row_line}
                  style={{ gridRow: rowIndex + 2, gridColumn: "1 / -1" }}
                />
              ))}
              {columnCoaches.map((coach, columnIndex) => (
                <div
                  key={`column-line-${coach.id}`}
                  className={styles.column_line}
                  style={{
                    gridRow: `2 / span ${rows.length}`,
                    gridColumn: columnIndex + 2,
                  }}
                />
              ))}

              {/* Cells are placed explicitly rather than flowing, so a
                  programme can span the rows it covers without shifting
                  everything after it. Row 1 is the column heads. */}
              {rows.map((row, rowIndex) => (
                <React.Fragment key={row.timeId}>
                  <div
                    className={styles.gutter_cell}
                    style={{ gridRow: rowIndex + 2, gridColumn: 1 }}
                  >
                    {row.hourLabel && (
                      <span className={styles.hour_label}>{row.hourLabel}</span>
                    )}
                  </div>
                  {columnCoaches.map((coach, columnIndex) => {
                    if (coveredCells.has(`${coach.id}-${rowIndex}`)) {
                      return null;
                    }

                    const cell = slotAt(visibleGrid, coach.id, row.timeId);
                    const position = {
                      gridRow: rowIndex + 2,
                      gridColumn: columnIndex + 2,
                    };

                    return cell ? (
                      <button
                        key={`${coach.id}-${row.timeId}`}
                        type="button"
                        className={styles.slot_cell}
                        style={position}
                        onClick={() => selectCell(coach, cell.startHour)}
                        data-testid="facility-calendar-slot"
                      >
                        {cell.startHour.toFormat("h:mm a")}
                      </button>
                    ) : (
                      // Blank: either the coach is not offering then, or the time
                      // is taken by a lesson or an unavailability.
                      <div
                        key={`${coach.id}-${row.timeId}`}
                        className={styles.empty_cell}
                        style={position}
                      />
                    );
                  })}
                </React.Fragment>
              ))}

              {programPlacements.map(({ program, coachId, startIndex, span }) => {
                const columnIndex = columnCoaches.findIndex(
                  (coach) => coach.id === coachId,
                );
                return (
                  // The block itself opens the programme — its title and time are
                  // all it needs to say, and registering happens on the page it
                  // opens rather than from a button squeezed into a calendar cell.
                  <button
                    key={program.id}
                    type="button"
                    className={styles.program_block}
                    style={{
                      gridRow: `${startIndex + 2} / span ${span}`,
                      gridColumn: columnIndex + 2,
                    }}
                    onClick={() =>
                      history.push(
                        isPublicPage
                          ? `/public/programs/${program.id}/view`
                          : `/programs/${program.id}/view`,
                      )
                    }
                    data-testid="facility-calendar-program"
                  >
                    <div className={styles.program_title}>{program.title}</div>
                    <div className={styles.program_time}>
                      {program.start.toFormat("h:mm a")}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default FacilityCalendarBody;
